import React, { useState, useEffect, useCallback } from "react";
import {
  Database,
  FileText,
  Code2,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  X,
  AlertTriangle,
} from "lucide-react";
import "./OnboardingWizard.css";

/* ─────────────────────────────────────────────
   STEP CONFIGURATION
   settingsPath ?tab= values must match TAB_PARAM_MAP keys in SettingsHub.jsx
───────────────────────────────────────────── */
const STEP_CONFIG = [
  {
    key: "Add a database configuration.",
    icon: Database,
    title: "Connect your database",
    short: "Database",
    description:
      "Link a data source so Geneilytics can read your business data and answer natural-language questions about it instantly.",
    tip: "Supports PostgreSQL, MySQL, BigQuery, Snowflake, Redshift, and more.",
    settingsPath: "/settings?tab=database",
    color: "#4f46e5",
    gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  },
  {
    key: "Add general instructions.",
    icon: FileText,
    title: "Add general instructions",
    short: "Instructions",
    description:
      "Tell Geneilytics about your business — terminology, KPIs, naming conventions, or any rules it should always follow.",
    tip: '"Revenue means net revenue after returns." Great instructions produce consistently accurate answers.',
    settingsPath: "/settings?tab=instructions",
    color: "#0284c7",
    gradient: "linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)",
  },
  {
    key: "Add SQL expressions.",
    icon: Code2,
    title: "Add SQL expressions",
    short: "SQL Expressions",
    description:
      "Pre-define reusable calculated fields and complex expressions so every query stays consistent and correct.",
    tip: "Define once, reuse everywhere — ideal for fiscal-year logic, custom metrics, or complex joins.",
    settingsPath: "/settings?tab=expressions",
    color: "#059669",
    gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
  },
  {
    key: "Add query instructions.",
    icon: MessageSquare,
    title: "Add query instructions",
    short: "Query Rules",
    description:
      "Set intelligent defaults for how Geneilytics approaches specific question types or filters your data automatically.",
    tip: '"Always filter by the current fiscal year unless explicitly asked otherwise."',
    settingsPath: "/settings?tab=query-instructions",
    color: "#d97706",
    gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
  },
];

/* ─────────────────────────────────────────────
   PROGRESS BAR
───────────────────────────────────────────── */
function ProgressBar({ total, done }) {
  const pct = total === 0 ? 100 : Math.round((done / total) * 100);
  return (
    <div className="ob-progress-wrap">
      <div className="ob-progress-track">
        <div className="ob-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="ob-progress-label">
        {done} of {total} complete
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STEP RAIL
───────────────────────────────────────────── */
function StepRail({ steps, activeIdx, completedKeys, onSelect }) {
  return (
    <div className="ob-rail">
      {steps.map((step, i) => {
        const done   = completedKeys.has(step.key);
        const active = i === activeIdx;
        const Icon   = step.icon;
        return (
          <React.Fragment key={step.key}>
            <button
              className={`ob-rail-item ${active ? "ob-rail-active" : ""} ${done ? "ob-rail-done" : ""}`}
              onClick={() => onSelect(i)}
            >
              <span
                className="ob-rail-icon"
                style={
                  done
                    ? { background: "rgba(5,150,105,0.1)", borderColor: "rgba(5,150,105,0.25)" }
                    : active
                    ? { background: step.gradient, borderColor: "transparent" }
                    : {}
                }
              >
                {done
                  ? <CheckCircle2 size={14} color="#059669" />
                  : <Icon size={14} color={active ? "#fff" : step.color} />
                }
              </span>
              <span className="ob-rail-label">{step.short}</span>
              {done && <span className="ob-rail-check">✓</span>}
            </button>
            {i < steps.length - 1 && <div className="ob-rail-connector" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   STEP DETAIL
───────────────────────────────────────────── */
function StepDetail({ step, stepNum, totalSteps, done, onGoToSettings, isAdmin }) {
  const Icon = step.icon;
  return (
    <div className="ob-detail">
      <div className="ob-detail-inner">
        <div className="ob-detail-step-row">
          <span className="ob-detail-step-tag">Step {stepNum} of {totalSteps}</span>
          <div className="ob-detail-step-line" />
        </div>

        <div className="ob-detail-icon-wrap" style={{ background: step.gradient }}>
          <Icon size={24} color="#fff" />
        </div>

        <h2 className="ob-detail-title">{step.title}</h2>
        <p className="ob-detail-desc">{step.description}</p>

        <div className="ob-detail-tip">
          <Sparkles size={14} className="ob-detail-tip-icon" />
          <span>{step.tip}</span>
        </div>

        {done ? (
          <div className="ob-detail-done-badge">
            <CheckCircle2 size={15} />
            Step completed
          </div>
        ) : isAdmin ? (
          <button
            className="ob-detail-cta"
            style={{ background: step.gradient }}
            onClick={() => onGoToSettings(step.settingsPath)}
          >
            Configure now <ArrowRight size={15} />
          </button>
        ) : (
          <div className="ob-detail-non-admin">
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>Only workspace admins can configure this step. Contact your admin to proceed.</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ALL DONE PANEL
───────────────────────────────────────────── */
function AllDonePanel({ onClose }) {
  return (
    <div className="ob-all-done">
      <div className="ob-all-done-icon">
        <CheckCircle2 size={36} color="#059669" />
      </div>
      <h2>You're all set!</h2>
      <p>Your workspace is fully configured. Start asking questions about your data.</p>
      <button className="ob-all-done-btn" onClick={onClose}>
        Start exploring <ArrowRight size={15} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT

   Props:
     pendingSetupItems  string[]   — keys of steps NOT yet completed (from server)
     isTenantAdmin      boolean
     onClose            () => void
     onNavigate         (path: string) => void
     onRefreshPending   () => void  — called when wizard needs fresh data from server
                                      (e.g. after user returns from settings tab)
───────────────────────────────────────────── */
export default function OnboardingWizard({
  pendingSetupItems = [],
  isTenantAdmin     = false,
  onClose,
  onNavigate,
  onRefreshPending,  // ← NEW: parent should re-fetch pendingSetupItems when called
}) {
  const [activeIdx,      setActiveIdx]      = useState(0);
  const [optimisticDone, setOptimisticDone] = useState(new Set()); // keys marked done before server confirms
  const [visible,        setVisible]        = useState(false);
  const [closing,        setClosing]        = useState(false);
  const [detailKey,      setDetailKey]      = useState(0);

  // ─── Animate in ───
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  // ─── KEY FIX #1 ───────────────────────────────────────────────────────────
  // Derive completed keys directly from props.
  // A step is complete when its key is ABSENT from pendingSetupItems (server says so)
  // OR we marked it done optimistically while waiting for the server to confirm.
  const pendingSet = new Set(pendingSetupItems);

  const serverCompletedKeys = new Set(
    STEP_CONFIG.filter((s) => !pendingSet.has(s.key)).map((s) => s.key)
  );

  // Merge server state + optimistic local state
  const completedKeys = new Set([...serverCompletedKeys, ...optimisticDone]);

  // Always show ALL steps — completed ones stay visible as done
  const allSteps = STEP_CONFIG;

  // ─── KEY FIX #2 ───────────────────────────────────────────────────────────
  // When the user leaves to configure a setting and comes back, re-fetch
  // pendingSetupItems so the wizard reflects the real server state immediately
  // without requiring a full page refresh.
  useEffect(() => {
    if (!onRefreshPending) return;

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        onRefreshPending();
      }
    }
    function handleFocus() {
      onRefreshPending();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [onRefreshPending]);

  // ─── Auto-advance active step ─────────────────────────────────────────────
  // When the current step becomes completed (server confirmed), move to the
  // next incomplete step automatically.
  useEffect(() => {
    const currentStep = allSteps[activeIdx];
    if (currentStep && completedKeys.has(currentStep.key)) {
      const nextIncomplete = allSteps.findIndex(
        (s, i) => i > activeIdx && !completedKeys.has(s.key)
      );
      if (nextIncomplete !== -1) {
        setActiveIdx(nextIncomplete);
        setDetailKey((k) => k + 1);
      }
    }
  }, [completedKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const allDone    = allSteps.every((s) => completedKeys.has(s.key));
  const activeStep = allSteps[activeIdx];
  const doneCount  = completedKeys.size;

  function handleClose() {
    setClosing(true);
    setTimeout(() => onClose?.(), 320);
  }

  function goToStep(idx) {
    setActiveIdx(idx);
    setDetailKey((k) => k + 1);
  }

  function handleGoToSettings(path) {
    // Mark optimistically so the UI updates immediately — server will confirm on next refresh
    if (activeStep) {
      setOptimisticDone((prev) => new Set([...prev, activeStep.key]));
    }
    if (onNavigate) {
      onNavigate(path);
    } else {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
    handleClose();
  }

  function handleNext() {
    if (activeIdx < allSteps.length - 1) goToStep(activeIdx + 1);
  }

  function handleBack() {
    if (activeIdx > 0) goToStep(activeIdx - 1);
  }

  return (
    <div
      className={`ob-backdrop ${visible ? "ob-visible" : ""} ${closing ? "ob-closing" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="ob-modal" role="dialog" aria-modal="true" aria-label="Workspace setup">

        <button className="ob-close-btn" onClick={handleClose} aria-label="Close">
          <X size={14} />
        </button>

        {/* Header */}
        <div className="ob-header">
          <div className="ob-header-left">
            <span className="ob-badge">
              <span className="ob-badge-dot" />
              Setup required
            </span>
            <h1 className="ob-header-title">Let's get your workspace ready</h1>
            <p className="ob-header-sub">
              Complete {allSteps.length} step{allSteps.length !== 1 ? "s" : ""} to unlock full querying.
            </p>
          </div>
          <ProgressBar total={allSteps.length} done={doneCount} />
        </div>

        {/* Body */}
        <div className="ob-body">
          {allDone ? (
            <AllDonePanel onClose={handleClose} />
          ) : (
            <>
              <StepRail
                steps={allSteps}
                activeIdx={activeIdx}
                completedKeys={completedKeys}
                onSelect={goToStep}
              />
              {activeStep && (
                <StepDetail
                  key={detailKey}
                  step={activeStep}
                  stepNum={activeIdx + 1}
                  totalSteps={allSteps.length}
                  done={completedKeys.has(activeStep.key)}
                  onGoToSettings={handleGoToSettings}
                  isAdmin={isTenantAdmin}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!allDone && (
          <div className="ob-footer">
            <span className="ob-footer-hint">
              {doneCount === 0
                ? "Click a step to begin"
                : `${doneCount} of ${allSteps.length} done`}
            </span>

            <div className="ob-footer-dots">
              {allSteps.map((s, i) => (
                <button
                  key={i}
                  className={`ob-dot ${i === activeIdx ? "ob-dot-active" : ""} ${
                    completedKeys.has(s.key) ? "ob-dot-done" : ""
                  }`}
                  onClick={() => goToStep(i)}
                  aria-label={`Go to step ${i + 1}`}
                />
              ))}
            </div>

            <div className="ob-footer-nav">
              {activeIdx > 0 && (
                <button className="ob-footer-btn" onClick={handleBack}>
                  <ChevronLeft size={13} /> Back
                </button>
              )}
              {activeIdx < allSteps.length - 1 && (
                <button className="ob-footer-btn ob-footer-btn-primary" onClick={handleNext}>
                  Next <ChevronRight size={13} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
